import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AuthPage from './components/Auth/AuthPage';
import MainLayout from './components/MainLayout';
import EmailConfirm from './components/Auth/EmailConfirm';
import ResetPassword from './components/Auth/ResetPassword';
import LegalPage from './components/Legal/LegalPage';
import AdminPanel from './components/Admin/AdminPanel';
import CallManager from './components/Call/CallManager';
import { useAuthStore } from './hooks/useAuthStore';
import { isDemoMode } from './services/demo';

function ProtectedRoute({ children }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated && !isDemoMode()) return <Navigate to="/login" />;
  // Администратору основной интерфейс мессенджера недоступен — только админ-панель.
  if (user?.isAdmin) return <Navigate to="/admin" />;
  return children;
}

function AdminRoute({ children }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user?.isAdmin) return <Navigate to="/" />;
  return children;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorText: '' };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, errorText: String((error && (error.stack || error.message)) || error) };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
    try {
      fetch('/api/debug/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: String((error && (error.stack || error.message)) || error).slice(0, 2000),
          url: window.location.href.slice(0, 300),
          source: 'error-boundary',
        }),
      }).catch(() => {});
    } catch (e) {}
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 16, padding: '0 16px' }}>
          <h2>Что-то пошло не так</h2>
          <p style={{ color: '#666' }}>Попробуйте обновить страницу</p>
          <p style={{ color: '#999', fontSize: 11, maxWidth: 480, wordBreak: 'break-all', textAlign: 'center' }}>
            {this.state.errorText}
          </p>
          <button onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }}
            style={{ padding: '10px 20px', background: '#1a1a2e', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
            Войти заново
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const { checkAuth, isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated) {
      checkAuth();
    }
  }, []);

  return (
    <ErrorBoundary>
      <CallManager />
      <Routes>
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />
        <Route path="/confirm-email" element={<EmailConfirm />} />
        <Route path="/confirm" element={<EmailConfirm />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy" element={<LegalPage type="privacy" />} />
        <Route path="/terms" element={<LegalPage type="terms" />} />
        <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
        <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
